// src/pages/app/EditPunches.js
import React, { useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/AdminPageHeader";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { db, functions } from "../../lib/firebase";
import { httpsCallable } from "firebase/functions";
import useUserRole from "../../hooks/useUserRole";
import useRolePermissions from "../../hooks/useRolePermissions";

const normalize = (s) => String(s || "").trim().toLowerCase();
const pad2 = (n) => String(n).padStart(2, "0");

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function getDefaultPayRateForUser(user = {}, payConfig = {}) {
  const position = user?.position || "";
  const base = payConfig?.positions?.[position];

  if (typeof base !== "number") return null;

  let rate = Number(base);
  const bump = Number(payConfig?.supervisorBump || 0);

  if (String(user?.role || "").toLowerCase() === "supervisor") {
    rate += bump;
  }

  return rate;
}

function localDateTimeFrom(dateISO, timeHM) {
  const [y, m, d] = String(dateISO || "").split("-").map((x) => parseInt(x, 10));
  const [hh, mm] = String(timeHM || "00:00").split(":").map((x) => parseInt(x, 10));
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
}

function toDate(ts) {
  if (!ts) return null;
  if (typeof ts?.toDate === "function") return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDateISO(ts) {
  const d = toDate(ts) || new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toTimeHM(ts) {
  const d = toDate(ts);
  if (!d) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function prettyDateTime(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function prettyDate(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

function hoursBetween(startTs, endTs) {
  const start = toDate(startTs);
  const end = toDate(endTs);
  if (!start || !end) return 0;
  return Math.max(0, (end.getTime() - start.getTime()) / 3600000);
}

function getPunchHours(p) {
  if (typeof p.hoursWorked === "number") return p.hoursWorked;
  return hoursBetween(p.startTime, p.endTime);
}

function getPunchRate(p) {
  if (typeof p.payRate === "number") return p.payRate;
  if (typeof p.rateUsed === "number") return p.rateUsed;
  return null;
}

function getPunchPay(p) {
  if (typeof p.totalPay === "number") return p.totalPay;
  const hrs = getPunchHours(p);
  const rate = getPunchRate(p);
  if (typeof rate === "number") return hrs * rate;
  return 0;
}

function formatMoney(value) {
  const n = Number(value || 0);
  return `$${n.toFixed(2)}`;
}

function uiButtonStyle(primary = false, danger = false) {
  if (danger) {
    return {
      border: "none",
      borderRadius: 14,
      padding: "12px 14px",
      fontWeight: 900,
      cursor: "pointer",
      background: "#dc2626",
      color: "#fff",
      fontSize: 14,
    };
  }

  return {
    border: primary ? "none" : "1px solid rgba(15, 23, 42, 0.12)",
    borderRadius: 14,
    padding: "12px 14px",
    fontWeight: 900,
    cursor: "pointer",
    background: primary ? "#0ea5e9" : "#fff",
    color: primary ? "#fff" : "#0f172a",
    fontSize: 14,
  };
}

function Sheet({ open, onClose, title, subtitle, children, isMobile, width = "min(900px, 100%)" }) {
  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.45)",
        display: "flex",
        alignItems: isMobile ? "flex-end" : "center",
        justifyContent: "center",
        padding: isMobile ? 0 : 16,
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? "100%" : width,
          maxHeight: isMobile ? "90vh" : "88vh",
          overflow: "auto",
          background: "#fff",
          borderRadius: isMobile ? "22px 22px 0 0" : 20,
          border: "1px solid rgba(15, 23, 42, 0.10)",
          boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)",
          padding: isMobile ? 16 : 20,
        }}
      >
        <div
          style={{
            width: 48,
            height: 5,
            borderRadius: 999,
            background: "#cbd5e1",
            margin: "0 auto 12px",
          }}
        />

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 20 }}>{title}</div>
            {subtitle ? (
              <div style={{ color: "#64748b", marginTop: 4, lineHeight: 1.5 }}>
                {subtitle}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontWeight: 900,
              fontSize: 20,
              color: "#334155",
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: 16 }}>{children}</div>
      </div>
    </div>
  );
}

export default function EditPunches() {
  const { role, loading: roleLoading } = useUserRole();
  const { can, loading: permsLoading } = useRolePermissions(role);

  const adminAddPunch = useMemo(() => httpsCallable(functions, "adminAddPunch"), []);
  const adminUpdatePunch = useMemo(() => httpsCallable(functions, "adminUpdatePunch"), []);
  const adminDeletePunch = useMemo(() => httpsCallable(functions, "adminDeletePunch"), []);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 640 : false
  );

  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [search, setSearch] = useState("");

  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [punches, setPunches] = useState([]);

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [editing, setEditing] = useState(null);
  const [editDateISO, setEditDateISO] = useState(todayISO());
  const [editStart, setEditStart] = useState("06:00");
  const [editEnd, setEditEnd] = useState("");
  const [editPayRate, setEditPayRate] = useState("");
  const [editReason, setEditReason] = useState("");
  const [saving, setSaving] = useState(false);

  const [showAddPunch, setShowAddPunch] = useState(false);
  const [addEmployeeUid, setAddEmployeeUid] = useState("");
  const [addDateISO, setAddDateISO] = useState(todayISO());
  const [addStart, setAddStart] = useState("06:00");
  const [addEnd, setAddEnd] = useState("");
  const [addPayRate, setAddPayRate] = useState("");
  const [addReason, setAddReason] = useState("");

  const [deletePunch, setDeletePunch] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth <= 640);
    }

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (roleLoading || permsLoading) return;
    if (!can("EditPunches")) return;
    loadPayrollPunches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLoading, permsLoading]);

  async function loadPayrollPunches() {
    setLoading(true);
    setPageError("");

    try {
      const usersSnap = await getDocs(collection(db, "users"));
      const payRatesSnap = await getDoc(doc(db, "appConfig", "payRates"));
      const payConfig = payRatesSnap.exists() ? payRatesSnap.data() : {};

      const usersByUid = new Map(
        usersSnap.docs.map((d) => {
          const data = d.data() || {};
          return [
            d.id,
            {
              uid: d.id,
              displayName:
                data.displayName ||
                data.name ||
                data.applicationData?.name ||
                `${data.applicationData?.firstName || ""} ${data.applicationData?.lastName || ""}`.trim() ||
                "Unnamed Employee",
              email: data.email || data.applicationData?.email || "",
              position: data.position || data.applicationData?.position || "",
              role: data.role || "",
              defaultPayRate: getDefaultPayRateForUser(
                {
                  position: data.position || data.applicationData?.position || "",
                  role: data.role || "",
                },
                payConfig
              ),
              active: data.active !== false && data.applicationData?.active !== false,
            },
          ];
        })
      );

      const allEmployeeRows = Array.from(usersByUid.values())
  .filter((u) => u.active !== false)
  .sort((a, b) =>
    String(a.displayName || "").localeCompare(String(b.displayName || ""))
  );

setAllEmployees(allEmployeeRows);

      const qPunches = query(
        collectionGroup(db, "entries"),
        orderBy("startTime", "desc"),
        limit(5000)
      );

      const punchesSnap = await getDocs(qPunches);

      const allPunches = punchesSnap.docs
        .map((d) => {
          const uid = d.ref.parent.parent?.id || "";
          const userInfo = usersByUid.get(uid) || {};

          return {
            id: d.id,
            uid,
            employeeName: userInfo.displayName || "Unknown Employee",
            employeeEmail: userInfo.email || "",
            employeePosition: userInfo.position || "",
            employeeActive: userInfo.active !== false,
            defaultPayRate: userInfo.defaultPayRate ?? null,
            ...d.data(),
          };
        })
        .filter((p) => !p.deleted);

      setPunches(allPunches);

      const byEmployee = new Map();

      for (const p of allPunches) {
        if (!byEmployee.has(p.uid)) {
          byEmployee.set(p.uid, {
            uid: p.uid,
            name: p.employeeName,
            email: p.employeeEmail,
            position: p.employeePosition,
            active: p.employeeActive,
            punches: [],
            punchCount: 0,
            totalHours: 0,
            totalPay: 0,
            latestStartTime: p.startTime,
          });
        }

        const bucket = byEmployee.get(p.uid);
        bucket.punches.push(p);
        bucket.punchCount += 1;
        bucket.totalHours += getPunchHours(p);
        bucket.totalPay += getPunchPay(p);

        const currentLatest = toDate(bucket.latestStartTime);
        const thisDate = toDate(p.startTime);
        if (thisDate && currentLatest && thisDate.getTime() > currentLatest.getTime()) {
          bucket.latestStartTime = p.startTime;
        }
      }

      const employeeRows = Array.from(byEmployee.values()).sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );

      setEmployees(employeeRows);
    } catch (e) {
      console.error("Failed to load payroll punches:", e);
      setPageError(`Failed to load punches: ${e?.message || String(e)}`);
    } finally {
      setLoading(false);
    }
  }

  const filteredEmployees = useMemo(() => {
    const q = normalize(search);
    if (!q) return employees;

    return employees.filter((emp) => {
      return (
        normalize(emp.name).includes(q) ||
        normalize(emp.email).includes(q) ||
        normalize(emp.position).includes(q)
      );
    });
  }, [employees, search]);

  const selectedPunches = useMemo(() => {
    if (!selectedEmployee) return [];

    return punches
      .filter((p) => p.uid === selectedEmployee.uid)
      .sort((a, b) => {
        const ad = toDate(a.startTime)?.getTime() || 0;
        const bd = toDate(b.startTime)?.getTime() || 0;
        return bd - ad;
      });
  }, [punches, selectedEmployee]);

  const totals = useMemo(() => {
    return filteredEmployees.reduce(
      (sum, emp) => {
        sum.punches += emp.punchCount;
        sum.hours += emp.totalHours;
        sum.pay += emp.totalPay;
        return sum;
      },
      { punches: 0, hours: 0, pay: 0 }
    );
  }, [filteredEmployees]);

  function openEmployee(emp) {
    setSelectedEmployee(emp);
  }

  function openEdit(p) {
    setEditing(p);
    setEditDateISO(toDateISO(p.startTime));
    setEditStart(toTimeHM(p.startTime));
    setEditEnd(p.endTime ? toTimeHM(p.endTime) : "");
    const existingRate = getPunchRate(p);

const fallbackRate =
  typeof p.defaultPayRate === "number"
    ? p.defaultPayRate
    : "";

setEditPayRate(
  typeof existingRate === "number"
    ? String(existingRate)
    : fallbackRate !== ""
    ? String(fallbackRate)
    : ""
);
    setEditReason("");
    setPageError("");
  }

  function closeEdit() {
    setEditing(null);
    setEditReason("");
    setEditPayRate("");
  }

  async function saveEdit() {
  if (!editing) return;

  if (!editReason.trim()) {
    setPageError("Please enter an edit reason.");
    return;
  }

  const newStartDT = localDateTimeFrom(editDateISO, editStart);
  const newEndDT = editEnd ? localDateTimeFrom(editDateISO, editEnd) : null;

  if (newEndDT && newEndDT.getTime() < newStartDT.getTime()) {
    setPageError("OUT time cannot be before IN time.");
    return;
  }

  setSaving(true);
  setPageError("");

  try {
    await adminUpdatePunch({
      targetUid: editing.uid,
      entryId: editing.id,
      startTime: newStartDT.toISOString(),
      endTime: newEndDT ? newEndDT.toISOString() : null,
      payRate: editPayRate !== "" ? Number(editPayRate) : null,
      note: "",
      reason: editReason.trim(),
    });

    closeEdit();
    await loadPayrollPunches();
  } catch (e) {
    console.error("Save failed:", e);
    setPageError(`Save failed: ${e?.message || String(e)}`);
  } finally {
    setSaving(false);
  }
}

async function saveAddPunch() {
  if (!addEmployeeUid) {
    setPageError("Please select an employee.");
    return;
  }

  if (!addReason.trim()) {
    setPageError("Please enter a reason.");
    return;
  }

  const startDT = localDateTimeFrom(addDateISO, addStart);
  const endDT = addEnd ? localDateTimeFrom(addDateISO, addEnd) : null;

  if (endDT && endDT.getTime() < startDT.getTime()) {
    setPageError("OUT time cannot be before IN time.");
    return;
  }

  setSaving(true);
  setPageError("");

  try {
    await adminAddPunch({
      targetUid: addEmployeeUid,
      startTime: startDT.toISOString(),
      endTime: endDT ? endDT.toISOString() : null,
      payRate: addPayRate !== "" ? Number(addPayRate) : null,
      note: "",
      reason: addReason.trim(),
    });

    setShowAddPunch(false);
    setAddEmployeeUid("");
    setAddDateISO(todayISO());
    setAddStart("06:00");
    setAddEnd("");
    setAddPayRate("");
    setAddReason("");

    await loadPayrollPunches();
  } catch (e) {
    console.error("Add punch failed:", e);
    setPageError(`Add punch failed: ${e?.message || String(e)}`);
  } finally {
    setSaving(false);
  }
}

  function openDelete(p) {
    setDeletePunch(p);
    setDeleteReason("");
    setPageError("");
  }

  function closeDelete() {
    setDeletePunch(null);
    setDeleteReason("");
  }

  async function confirmDelete() {
    if (!deletePunch) return;

    if (!deleteReason.trim()) {
      setPageError("Please enter a delete reason.");
      return;
    }

    setSaving(true);
    setPageError("");

    try {
      await adminDeletePunch({
        targetUid: deletePunch.uid,
        entryId: deletePunch.id,
        reason: deleteReason.trim(),
      });

      closeDelete();
      await loadPayrollPunches();
    } catch (e) {
      console.error("Delete failed:", e);
      setPageError(`Delete failed: ${e?.message || String(e)}`);
    } finally {
      setSaving(false);
    }
  }

  if (roleLoading || permsLoading) {
    return <div style={{ padding: 20 }}>Loading…</div>;
  }

  if (!can("EditPunches")) {
    return (
      <div style={{ padding: 20 }}>
        <AdminPageHeader title="Payroll Punch List" />
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  const cardStyle = {
    background: "#fff",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    borderRadius: 18,
    boxShadow: "0 10px 18px rgba(15, 23, 42, 0.06)",
    padding: isMobile ? 14 : 18,
  };

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: 14,
    border: "1px solid rgba(15, 23, 42, 0.12)",
    boxSizing: "border-box",
    fontSize: 15,
    background: "#fff",
    minHeight: 46,
  };

  return (
    <div style={{ padding: isMobile ? 12 : 20, maxWidth: 1220, margin: "0 auto" }}>
      <AdminPageHeader title="Payroll Punch List" />

      <div style={{ color: "#64748b", marginTop: -8, marginBottom: 14 }}>
        All payroll punches by employee • Edit shift times and pay rates from one place
      </div>

      {pageError ? (
        <div
          style={{
            marginBottom: 12,
            padding: "12px 14px",
            borderRadius: 14,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontWeight: 800,
          }}
        >
          {pageError}
        </div>
      ) : null}

      <div style={cardStyle}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "minmax(260px, 1fr) auto auto auto",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Search</div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by employee, email, or position"
              style={inputStyle}
            />
          </div>

          <div>
            <div style={{ color: "#64748b", fontWeight: 800, marginBottom: 6 }}>
              Employees
            </div>
            <div style={{ fontWeight: 900 }}>{filteredEmployees.length}</div>
          </div>

          <div>
            <div style={{ color: "#64748b", fontWeight: 800, marginBottom: 6 }}>
              Punches
            </div>
            <div style={{ fontWeight: 900 }}>{totals.punches}</div>
          </div>

          <button
            type="button"
            onClick={loadPayrollPunches}
            disabled={loading}
            style={{ ...uiButtonStyle(true), minHeight: 46 }}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>

          <button
          type="button"
          onClick={() => setShowAddPunch(true)}
          disabled={loading || saving}
          style={{ ...uiButtonStyle(false), minHeight: 46 }}
        >
          + Add Punch
        </button>

        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {[
                  "Employee",
                  "Email",
                  "Position",
                  "Punches",
                  "Hours",
                  "Pay",
                  "Latest Punch",
                  "",
                ].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: "left",
                      padding: "10px 8px",
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ padding: 16, color: "#64748b" }}>
                    Loading punches…
                  </td>
                </tr>
              ) : filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 16, color: "#64748b" }}>
                    No employees with punches found.
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((emp) => (
                  <tr key={emp.uid}>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9", fontWeight: 900 }}>
                      {emp.name}
                      {!emp.active ? (
                        <span style={{ marginLeft: 8, color: "#b91c1c", fontSize: 12 }}>
                          Inactive
                        </span>
                      ) : null}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {emp.email}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {emp.position}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {emp.punchCount}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {emp.totalHours.toFixed(2)}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {formatMoney(emp.totalPay)}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      {prettyDate(emp.latestStartTime)}
                    </td>
                    <td style={{ padding: "10px 8px", borderBottom: "1px solid #f1f5f9" }}>
                      <button
                        type="button"
                        onClick={() => openEmployee(emp)}
                        style={uiButtonStyle(false)}
                      >
                        View Punches
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {filteredEmployees.length > 0 ? (
              <tfoot>
                <tr style={{ background: "#f8fafc", fontWeight: 900 }}>
                  <td colSpan={3} style={{ padding: "12px 8px" }}>Totals</td>
                  <td style={{ padding: "12px 8px" }}>{totals.punches}</td>
                  <td style={{ padding: "12px 8px" }}>{totals.hours.toFixed(2)}</td>
                  <td style={{ padding: "12px 8px" }}>{formatMoney(totals.pay)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>

      <Sheet
        open={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        title={selectedEmployee?.name || "Employee Punches"}
        subtitle={
          selectedEmployee
            ? `${selectedEmployee.email || "No email"} • ${selectedPunches.length} punch${selectedPunches.length === 1 ? "" : "es"}`
            : ""
        }
        isMobile={isMobile}
      >
        <div style={{ display: "grid", gap: 10 }}>
          {selectedPunches.map((p) => {
            const hrs = getPunchHours(p);
            const rate = getPunchRate(p);
            const pay = getPunchPay(p);
            const isOpen = !p.endTime || p.open;

            return (
              <div
                key={p.id}
                style={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 16,
                  background: isOpen ? "#ecfeff" : "#f8fafc",
                  padding: 14,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1.5fr 0.7fr 0.7fr 0.8fr auto",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                      IN
                    </div>
                    <div style={{ fontWeight: 900 }}>{prettyDateTime(p.startTime)}</div>
                  </div>

                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                      OUT
                    </div>
                    <div style={{ fontWeight: 900 }}>
                      {p.endTime ? prettyDateTime(p.endTime) : "Open"}
                    </div>
                  </div>

                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                      Hours
                    </div>
                    <div style={{ fontWeight: 900 }}>{hrs.toFixed(2)}</div>
                  </div>

                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                      Rate
                    </div>
                    <div style={{ fontWeight: 900 }}>
                      {typeof rate === "number" ? formatMoney(rate) : "—"}
                    </div>
                  </div>

                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
                      Pay
                    </div>
                    <div style={{ fontWeight: 900 }}>{formatMoney(pay)}</div>
                  </div>

                  <div style={{ display: "flex", gap: 8, justifyContent: isMobile ? "flex-start" : "flex-end" }}>
                    <button type="button" onClick={() => openEdit(p)} style={uiButtonStyle(false)}>
                      Edit
                    </button>
                    <button type="button" onClick={() => openDelete(p)} style={uiButtonStyle(false, true)}>
                      Delete
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 8, color: "#64748b", fontSize: 12 }}>
                  Punch ID: {p.id}
                </div>
              </div>
            );
          })}
        </div>
      </Sheet>

      <Sheet
        open={!!editing}
        onClose={() => {
          if (saving) return;
          closeEdit();
        }}
        title="Edit Punch"
        subtitle={
          editing
            ? `${editing.employeeName} • ${prettyDateTime(editing.startTime)}`
            : ""
        }
        isMobile={isMobile}
        width="min(760px, 100%)"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <div>
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
              Date
            </div>
            <input
              type="date"
              value={editDateISO}
              onChange={(e) => setEditDateISO(e.target.value)}
              style={inputStyle}
              disabled={saving}
            />
          </div>

          <div>
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
              IN
            </div>
            <input
              type="time"
              value={editStart}
              onChange={(e) => setEditStart(e.target.value)}
              style={inputStyle}
              disabled={saving}
            />
          </div>

          <div>
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
              OUT
            </div>
            <input
              type="time"
              value={editEnd}
              onChange={(e) => setEditEnd(e.target.value)}
              style={inputStyle}
              disabled={saving}
            />
          </div>

          <div>
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
              Pay Rate
            </div>
            <input
              type="number"
              step="0.01"
              value={editPayRate}
              onChange={(e) => setEditPayRate(e.target.value)}
              style={inputStyle}
              disabled={saving}
            />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
            Edit Reason
          </div>
          <textarea
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder="Reason required for audit"
            style={{
              ...inputStyle,
              minHeight: 110,
              resize: "vertical",
              fontFamily: "inherit",
            }}
            disabled={saving}
          />
        </div>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={closeEdit} disabled={saving} style={uiButtonStyle(false)}>
            Cancel
          </button>
          <button type="button" onClick={saveEdit} disabled={saving} style={uiButtonStyle(true)}>
            Save Changes
          </button>
        </div>
      </Sheet>

      <Sheet
  open={showAddPunch}
  onClose={() => {
    if (saving) return;
    setShowAddPunch(false);
  }}
  title="Add Punch"
  subtitle="Manually add a payroll punch for an employee"
  isMobile={isMobile}
  width="min(760px, 100%)"
>
  <div
    style={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
      gap: 12,
    }}
  >
    <div>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
        Employee
      </div>
      <select
        value={addEmployeeUid}
        onChange={(e) => {
          const uid = e.target.value;
          setAddEmployeeUid(uid);

        const emp = allEmployees.find((x) => x.uid === uid);

if (emp?.defaultPayRate != null) {
  setAddPayRate(String(emp.defaultPayRate));
} else {
  setAddPayRate("");
}  
        }}
        style={inputStyle}
        disabled={saving}
      >
        <option value="">Select employee</option>
       {allEmployees.map((emp) => (
  <option key={emp.uid} value={emp.uid}>
    {emp.displayName || emp.name || emp.email || "Unnamed Employee"}
    {emp.position ? ` — ${emp.position}` : ""}
  </option>
))} 
      </select>
    </div>

    <div>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
        Date
      </div>
      <input
        type="date"
        value={addDateISO}
        onChange={(e) => setAddDateISO(e.target.value)}
        style={inputStyle}
        disabled={saving}
      />
    </div>

    <div>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
        IN
      </div>
      <input
        type="time"
        value={addStart}
        onChange={(e) => setAddStart(e.target.value)}
        style={inputStyle}
        disabled={saving}
      />
    </div>

    <div>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
        OUT
      </div>
      <input
        type="time"
        value={addEnd}
        onChange={(e) => setAddEnd(e.target.value)}
        style={inputStyle}
        disabled={saving}
      />
    </div>

    <div>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
        Pay Rate
      </div>
      <input
        type="number"
        step="0.01"
        value={addPayRate}
        onChange={(e) => setAddPayRate(e.target.value)}
        style={inputStyle}
        disabled={saving}
      />
    </div>
  </div>

  <div style={{ marginTop: 12 }}>
    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
      Reason
    </div>
    <textarea
      value={addReason}
      onChange={(e) => setAddReason(e.target.value)}
      placeholder="Reason required for audit"
      style={{
        ...inputStyle,
        minHeight: 110,
        resize: "vertical",
        fontFamily: "inherit",
      }}
      disabled={saving}
    />
  </div>

  <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
    <button
      type="button"
      onClick={() => setShowAddPunch(false)}
      disabled={saving}
      style={uiButtonStyle(false)}
    >
      Cancel
    </button>

    <button
      type="button"
      onClick={saveAddPunch}
      disabled={saving}
      style={uiButtonStyle(true)}
    >
      Save Punch
    </button>
  </div>
</Sheet>

      <Sheet
        open={!!deletePunch}
        onClose={() => {
          if (saving) return;
          closeDelete();
        }}
        title="Delete Punch"
        subtitle={deletePunch ? `${deletePunch.employeeName} • ${prettyDateTime(deletePunch.startTime)}` : ""}
        isMobile={isMobile}
        width="min(560px, 100%)"
      >
        <div style={{ color: "#991b1b", fontWeight: 800, marginBottom: 12 }}>
          This will remove the punch from payroll and timecards.
        </div>

        <div>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
            Delete Reason
          </div>
          <textarea
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Reason required for audit"
            style={{
              ...inputStyle,
              minHeight: 110,
              resize: "vertical",
              fontFamily: "inherit",
            }}
            disabled={saving}
          />
        </div>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={closeDelete} disabled={saving} style={uiButtonStyle(false)}>
            Cancel
          </button>
          <button type="button" onClick={confirmDelete} disabled={saving} style={uiButtonStyle(false, true)}>
            Delete Punch
          </button>
        </div>
      </Sheet>
    </div>
  );
}